import { Grid2, Stack, Typography } from '@mui/material';
import { FieldArray, Form, Formik } from 'formik';
import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';
import type { Schema } from 'yup';
import { array, boolean, object, string } from 'yup';

import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import RoundedButton from '@components/RoundedButton';
import SkeletonLoader from '@components/SkeletonLoader';

import { isDevelopment } from '@constants/env';

import {
  useGetUserAccessManagement,
  useUpsertUserAccessManagement,
} from '@hooks/api/access-management';

import type { FormValues } from '@models/access-management';

import type {
  PermissionControlResponse,
  PermissionControlResponseNode,
} from 'generated/types';
import { PermissionControlScope } from 'generated/types';

import UserAccessForm from './UserAccessForm';

const messages = defineMessages({
  title: {
    id: 'accessManagement.accessManagement',
    defaultMessage: 'Access Management',
  },
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
});
interface RecordsAndFeaturesAccessProps {
  accessingUserId?: string;
  ownerUserId?: string;
}

function RecordsAndFeaturesAccess({
  accessingUserId = '',
  ownerUserId = '',
}: RecordsAndFeaturesAccessProps) {
  const { formatMessage } = useIntl();

  const {
    data: userAccessData,
    loading: getAccessManagementLoading,
    refetch,
  } = useGetUserAccessManagement({
    user_id: ownerUserId,
    accessing_user_id: accessingUserId,
  });

  const [upsertAccessManagement, upsertAccessManagementLoading] =
    useUpsertUserAccessManagement();

  const validationSchema: Schema<Partial<FormValues>> = object().shape({
    access: array().of(
      object().shape({
        control_set_id: string().required(),
        name: string().required(),
        grants: array()
          .of(
            object({
              grantName: string().required(),
              permission_control_id: string().required(),
              view: object()
                .shape({
                  scope: string().oneOf([
                    PermissionControlScope.write,
                    PermissionControlScope.read,
                  ]),
                  is_enabled: boolean(),
                })
                .nullable(),
              edit: object()
                .shape({
                  scope: string().oneOf([
                    PermissionControlScope.write,
                    PermissionControlScope.read,
                  ]),
                  is_enabled: boolean(),
                })
                .nullable(),
            }).required()
          )
          .required(),
      })
    ),
  });

  const initialValues: FormValues = {
    access: userAccessData,
    submit: null,
  };

  async function handleSubmit(values: FormValues) {
    if (!ownerUserId || !accessingUserId) {
      return;
    }

    let isChanged = false;
    try {
      for (const { control_set_id, grants } of values.access) {
        const [{ grants: initialGrants }] = initialValues.access.filter(
          (a) => a.control_set_id === control_set_id
        );

        // Only push changes, don't perform useless updates
        const changes = grants.filter(
          ({ permission_control_id, edit, view }) => {
            const initial = initialGrants.find(
              (grant) => grant.permission_control_id === permission_control_id
            );
            if (!initial) {
              return true;
            }
            return (
              initial.edit?.is_enabled !== edit?.is_enabled ||
              initial.view?.is_enabled !== view?.is_enabled
            );
          }
        );
        if (changes.length === 0) {
          continue;
        }
        isChanged = true;
        await upsertAccessManagement({
          owner_user_id: ownerUserId,
          input: {
            accessing_user_id: accessingUserId,
            user_id: ownerUserId,
            permission_control_set_id: control_set_id,
            grants: changes.reduce<PermissionControlResponse[]>((acc, curr) => {
              const { permission_control_id, edit, view } = curr;
              acc.push({
                permission_control_id,
                nodes: [
                  ...(edit
                    ? [
                        {
                          scope: edit.scope,
                          is_enabled: edit.is_enabled,
                        } as PermissionControlResponseNode,
                      ]
                    : []),
                  ...(view
                    ? [
                        {
                          scope: view.scope,
                          is_enabled: view.is_enabled,
                        } as PermissionControlResponseNode,
                      ]
                    : []),
                ],
              });
              return acc;
            }, []),
          },
        });
      }
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      if (isChanged) {
        await refetch({
          input: {
            user_id: ownerUserId,
            accessing_user_id: accessingUserId,
          },
        });
      }
    }
  }

  const loading = getAccessManagementLoading || upsertAccessManagementLoading;

  return (
    <Stack direction="row" spacing={2}>
      <SkeletonLoader
        count={2}
        loading={getAccessManagementLoading}
        height={400}
        width="100%"
      >
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ values, isSubmitting }) => {
            return (
              <Form>
                <FieldArray
                  name="access"
                  render={(arrayHelpers) => {
                    return (
                      <>
                        <Grid2 container spacing={2}>
                          {values.access.map((controlSet, index) => {
                            return (
                              <UserAccessForm
                                key={controlSet.control_set_id}
                                arrayHelpers={arrayHelpers}
                                index={index}
                                loading={loading}
                                controlSet={controlSet}
                              />
                            );
                          })}
                        </Grid2>
                        <ContentLoader loading={loading}>
                          <Grid2 size={{ xs: 12 }}>
                            <RoundedButton
                              disabled={isSubmitting || loading}
                              height="40px"
                              type="submit"
                            >
                              {formatMessage(messages.save)}
                            </RoundedButton>
                          </Grid2>
                        </ContentLoader>
                      </>
                    );
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </SkeletonLoader>
    </Stack>
  );
}

export default withErrorBoundary(RecordsAndFeaturesAccess, {
  FallbackComponent: (props) => {
    const { formatMessage } = useIntl();
    return (
      <>
        <Typography variant="subtitle2">
          {formatMessage(messages.title)}
        </Typography>
        <ErrorBoundaryFallback {...props} />
      </>
    );
  },
});
